import React from "react";

export const UNGROUPED_KEY = "ungrouped";

export function resolveActivityGroupId(row) {
  if (!row) return null;
  const raw = row.group_id ?? row.groupId ?? row.group?.id;
  if (raw == null || raw === "") return null;
  return raw;
}

export function activityDroppableId(groupId) {
  return groupId == null || groupId === "" ? UNGROUPED_KEY : `group-${groupId}`;
}

export function parseActivityDroppableId(droppableId) {
  if (!droppableId || droppableId === UNGROUPED_KEY) return null;
  const raw = String(droppableId).replace(/^group-/, "");
  if (raw === "" || raw === "null") return null;
  const asNum = Number(raw);
  return Number.isFinite(asNum) ? asNum : raw;
}

export function sortByRowOrder(a, b) {
  const ao = Number(a?.row_order);
  const bo = Number(b?.row_order);
  const aOk = Number.isFinite(ao);
  const bOk = Number.isFinite(bo);
  if (aOk && bOk && ao !== bo) return ao - bo;
  if (aOk && !bOk) return -1;
  if (!aOk && bOk) return 1;
  return (Number(a?.id) || 0) - (Number(b?.id) || 0);
}

export function inferGroupsFromActivities(activities = []) {
  const map = new Map();
  for (const row of activities) {
    const id = resolveActivityGroupId(row);
    if (id == null) continue;
    const key = String(id);
    if (map.has(key)) continue;
    const g = row.group || {};
    map.set(key, {
      id,
      title: g.title || "",
      row_order: g.row_order ?? 0,
      project_id: g.project_id,
      company_id: g.company_id,
    });
  }
  return Array.from(map.values()).sort(sortByRowOrder);
}

export function mergeActivityGroups(fetchedGroups, activities) {
  const byId = new Map();
  for (const group of fetchedGroups || []) {
    if (group?.id == null) continue;
    byId.set(String(group.id), { ...group });
  }
  for (const inferred of inferGroupsFromActivities(activities)) {
    if (!byId.has(String(inferred.id))) {
      byId.set(String(inferred.id), inferred);
    }
  }
  return Array.from(byId.values()).sort(sortByRowOrder);
}

/**
 * @returns {Array<{
 *   key: string,
 *   id: number|string|null,
 *   title: string,
 *   row_order: number,
 *   activities: Array,
 *   isUngrouped: boolean,
 * }>}
 */
export function buildActivityGroupSections(
  activities,
  groups,
  { ungroupedLabel = "", hideEmptyGroups = false, includeEmptyUngrouped = false } = {}
) {
  const merged = mergeActivityGroups(groups, activities);
  const buckets = new Map();
  for (const group of merged) {
    buckets.set(String(group.id), {
      key: activityDroppableId(group.id),
      id: group.id,
      title: group.title || "",
      row_order: group.row_order ?? 0,
      activities: [],
      isUngrouped: false,
    });
  }

  const ungrouped = {
    key: UNGROUPED_KEY,
    id: null,
    title: ungroupedLabel,
    row_order: Number.MAX_SAFE_INTEGER,
    activities: [],
    isUngrouped: true,
  };

  for (const row of activities || []) {
    const groupId = resolveActivityGroupId(row);
    if (groupId == null) {
      ungrouped.activities.push(row);
      continue;
    }
    const key = String(groupId);
    if (!buckets.has(key)) {
      buckets.set(key, {
        key: activityDroppableId(groupId),
        id: groupId,
        title: row.group?.title || "",
        row_order: row.group?.row_order ?? 0,
        activities: [],
        isUngrouped: false,
      });
    }
    buckets.get(key).activities.push(row);
  }

  for (const section of buckets.values()) {
    section.activities.sort(sortByRowOrder);
  }
  ungrouped.activities.sort(sortByRowOrder);

  let sections = Array.from(buckets.values()).sort(sortByRowOrder);
  if (hideEmptyGroups) {
    sections = sections.filter((section) => section.activities.length > 0);
  }

  const showUngrouped =
    ungrouped.activities.length > 0 ||
    (includeEmptyUngrouped && sections.length > 0) ||
    sections.length === 0;
  if (showUngrouped) sections.push(ungrouped);

  return sections;
}

export function activityMatchesQuery(row, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return [
    row?.display_title,
    row?.displayTitle,
    row?.title,
    row?.number,
    row?.description,
    row?.activity_type,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
}

export function filterActivityGroupSections(sections, predicate) {
  return (sections || [])
    .map((section) => ({
      ...section,
      activities: (section.activities || []).filter(predicate),
    }))
    .filter((section) => section.activities.length > 0);
}

export function hasRealActivityGroups(activities, groups) {
  if ((groups || []).some((group) => group?.id != null)) return true;
  return (activities || []).some((row) => resolveActivityGroupId(row) != null);
}

export function activityOptionLabel(row) {
  return row?.display_title || row?.displayTitle || row?.title || `#${row?.id}`;
}

/** Native <select> options with optgroups when activities belong to groups. */
export function GroupedActivityOptions({
  activities = [],
  groups = [],
  ungroupedLabel = "",
  emptyValue = "",
  emptyLabel,
}) {
  const sections = buildActivityGroupSections(activities, groups, {
    ungroupedLabel,
    hideEmptyGroups: true,
  });
  const showHeaders = hasRealActivityGroups(activities, groups);

  return (
    <>
      {emptyLabel != null ? <option value={emptyValue}>{emptyLabel}</option> : null}
      {showHeaders
        ? sections.map((section) => (
            <optgroup key={section.key} label={section.title || ungroupedLabel}>
              {section.activities.map((row) => (
                <option key={row.id} value={row.id}>
                  {activityOptionLabel(row)}
                </option>
              ))}
            </optgroup>
          ))
        : sections.flatMap((section) => section.activities).map((row) => (
            <option key={row.id} value={row.id}>
              {activityOptionLabel(row)}
            </option>
          ))}
    </>
  );
}

export const GROUP_HEADER_CLASS =
  "sticky top-0 z-[1] flex items-center justify-between gap-2 px-4 py-2.5 text-[12px] font-extrabold uppercase tracking-wide text-[#0B3A78] bg-[#EAF3FD] border-y border-[#BFDCF7]";

export function activityGroupTitle(row) {
  if (!row) return "";
  return row.group?.title || "";
}
